
















































import useAppConfig from "@/composables/useAppConfig";
import useMessagingFilters from "@/composables/useMessagingFilters";
import useTour from "@/composables/useTour";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import useMessagingStatusFilters from "@/composables/v2.1/useMessagingStatusFilters";
import useStoredMessages from "@/composables/v2.1/useStoredMessages";
import useChatInput from "@/composables/v3/useChatInput";
import useReadChat from "@/composables/v3/useReadChat";
import { mobilePluginBarActive, mqShallShowSidebarLeft } from "@/composables/v3/useUx";
import toastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import i18n from "@/libs/i18n";
import { useChannelsStore } from "@/store/useChannelsStore";
import { useChatsStore } from "@/store/useChatsStore";
import { useTagsStore } from "@/store/useTagsStore";
import { useTeamsStore } from "@/store/useTeamsStore";
import { useUsersStore } from "@/store/useUsersStore";
import { useContactsStore } from "@/store/useContactsStore";
import { ActiveFilter, StatusFilterNameType, StatusType } from "@/types/filter.types";
import { InboxModuleType } from "@/types/inboxModule.type";
import { decrypt } from "@/utils/crypto";
import StartChatPlaceholder from "@/views/components/inbox/startChatPlaceholder.vue";
import ChatAlerts from "@/views/modules/messaging-inbox/alerts/alerts.vue";
import ChatLog from "@/views/modules/messaging-inbox/chat-log/chatLog.vue";
import ChatEntryCompose from "@/views/modules/messaging-inbox/compose/compose.vue";
import Navbar from "@/views/modules/messaging-inbox/navbar/navbar.vue";
import SidebarLeft from "@/views/modules/messaging-inbox/sidebarLeft.vue";
import SidebarRight from "@/views/modules/messaging-inbox/sidebarRight.vue";
import TypingIndicator from "@/views/modules/messaging-inbox/typing-indicator/typingIndicator.vue";
import { chatLogPs, scrollToBottomInChatLog } from "@/views/modules/messaging-inbox/useChat";
import useTypingIndicators from "@/views/modules/messaging-inbox/useTypingIndicators";
import useUserSettings from "@/composables/useUserSettings";
// @ts-ignore
import useVerticalNavMenu from "@core/layouts/layout-vertical/components/vertical-nav-menu/useVerticalNavMenu";

// @ts-ignore
import { useRouter } from "@core/utils/utils";
import { RequestQueryBuilder } from "@rewiko/crud-request";
import {
    BaseWebsocketEvent,
    ChatListMode,
    ChatsListChat,
    WebsocketNewEntriesEvent,
    WebsocketTypingStartEvent,
    WebsocketTypingStopEvent,
    WebsocketUserUpdateEvent,
} from "@saysimple-platform/dtos";
// @ts-ignore
import { $themeBreakpoints } from "@themeConfig";
import { computed, defineComponent, nextTick, onMounted, onUnmounted, Ref, ref, watch } from "@vue/composition-api";
import { useBreakpoints, useDebounceFn } from "@vueuse/core";
import { BAvatar, BImg, BOverlay, BSpinner } from "bootstrap-vue";
import { storeToRefs } from "pinia";
import { useToast } from "vue-toastification/composition";

export default defineComponent({
    components: {
        BAvatar,
        BImg,
        BOverlay,
        ChatAlerts,
        ChatEntryCompose,
        ChatLog,
        Navbar,
        SidebarLeft,
        SidebarRight,
        StartChatPlaceholder,
        TypingIndicator,
        BSpinner,
    },
    sockets: {
        // @see https://justinternet.atlassian.net/wiki/spaces/SAYS/pages/647004161/Frontend+events
        socketEvent(socketMessage: { event: string; iv: string }) {
            const wsMessage: BaseWebsocketEvent = decrypt(socketMessage.event, socketMessage.iv);

            let payload;
            switch (wsMessage.type) {
                case "TYPING_START":
                    useTypingIndicators().addTypingIndicator(wsMessage.payload as WebsocketTypingStartEvent);

                    break;
                case "TYPING_STOP":
                    useTypingIndicators().removeTypingIndicator(wsMessage.payload as WebsocketTypingStopEvent);

                    break;
                case "NEW_ENTRY":
                    payload = wsMessage.payload as WebsocketNewEntriesEvent;

                    useTypingIndicators().removeTypingIndicator({
                        inboxModuleType: "messaging",
                        chatId: payload.chat.id,
                        userId: payload.entries.at(-1).createdById, // @maybe this is not the right one, but got no userId atm.
                    });

                    if (payload.contact?.id && payload.contact?.name) {
                        useChatsStore().updateContactName({
                            contactId: payload.contact.id,
                            name: payload.contact.name,
                        });
                    }

                    break;
                case "userUpdated":
                    payload = wsMessage.payload as WebsocketUserUpdateEvent;

                    useUsersStore().updateUserStatus(parseInt(payload.id), payload.updates.status);

                    break;
            }
        },
    },
    setup() {
        const toast = useToast();

        const { setInboxModuleType } = useInboxModuleType();
        setInboxModuleType(InboxModuleType.MESSAGING);

        const { fetchStoredMessages, activeChatMessage } = useStoredMessages();
        fetchStoredMessages();

        //----------------------------------------------------------------------

        useTagsStore().fetchTags(
            RequestQueryBuilder.create({
                fields: ["id", "name", "color"],
            })
        );

        useUsersStore().fetchUsers(
            RequestQueryBuilder.create({
                fields: ["id", "name", "status", "pictureUrl"],
                filter: [
                    { field: "active", operator: "$eq", value: 1 },
                    { field: "role", operator: "$ne", value: "saysimple" },
                    { field: "role", operator: "$ne", value: "api" },
                ],
                join: ["teams"],
            }),
            true
        );

        useTeamsStore().fetchTeams(
            RequestQueryBuilder.create({
                fields: ["id", "name", "color"],
                join: [{ field: "teamUsers", select: ["userId"] }],
            }),
            true
        );

        useChannelsStore().fetchChannels(
            RequestQueryBuilder.create({
                fields: ["id", "name", "provider", "platform", "customerCareWindowLength"],
                join: [
                    { field: "teamChannels", select: ["teamId"] },
                    { field: "userChannels", select: ["userId"] },
                ],
                search: {
                    platform: {
                        $ne: InboxModuleType.EMAIL,
                    },
                },
            }),
            true
        );

        useContactsStore().fetchLabels();

        //----------------------------------------------------------------------

        const { route, router } = useRouter();
        const routeUrl = computed(() => route.value.params);

        //----------------------------------------------------------------------

        // Close the active chat if we leave the module, so there are no issues with starting new chats etc.
        onUnmounted(() => chatsStore.$reset()); // @note $reset is a Pinia function.

        //----------------------------------------------------------------------

        onMounted(() => {
            const { shouldShow, hasSteps, startTour } = useTour("inbox");
            if (shouldShow() && hasSteps.value) {
                startTour();
            }

            // Don't know why this is here. This was coming from Neurosys, so copied it for now.
            const {
                shouldShow: teamChatShouldShow,
                hasSteps: teamChatHasSteps,
                startTour: teamChatStartTour,
            } = useTour("teamChat");
            if (teamChatShouldShow() && teamChatHasSteps.value) {
                teamChatStartTour();
            }

            // On small screens, if no chat id is set, we want to show the left sidebar instead of the zero state
            if (!router.currentRoute.params.chatId && useBreakpoints($themeBreakpoints).isSmaller("lg")) {
                mqShallShowSidebarLeft.value = true;
            }
        });

        // @todo filters need to be refactored. We now have 2 kind of filters through each other?
        const { getActiveFilter: activeFilter, filters, sortBy } = useMessagingFilters();
        const chatsStore = useChatsStore();

        const isFirstRun = ref(true);
        const debouncedFetchChatsAndContacts = useDebounceFn(
            (req: RequestQueryBuilder, setPage: number) => chatsStore.fetchChatsList(req, setPage),
            //200
            30
        );

        // Args wrapped in an object, because it's currently emitted from deeper components (until filters got refactored).
        const applySearchAndFilters = ({
            sortBy,
            activeFilter,
            filters,
        }: {
            sortBy: any;
            activeFilter: any;
            filters: any;
        }): void => {
            const { enforcedTeamFilters } = useMessagingFilters();
            const { isLoadingChats } = storeToRefs(chatsStore);
            isLoadingChats.value = true; // Set loading state here to avoid show loader after debounce delay.

            const search = { ...activeFilter.value.status?.query };

            if (filters.userIds.length > 0) {
                search.assignedUserId = { $in: filters.userIds };
            }
            if (filters.read.length > 0) {
                search.chatRead = { $in: filters.read };
            }
            if (filters.labelIds.length > 0) {
                search.chatLabelIds = { $in: filters.labelIds };
            }
            if (filters.channels.length > 0) {
                search.channelId = { $in: filters.channels };
            }
            if (filters.teams.length > 0) {
                search.assignedTeamId = { $in: filters.teams };
            } else if (enforcedTeamFilters.value.enabled && enforcedTeamFilters.value.teams.length > 0) {
                if (enforcedTeamFilters.value.includeUnassignedChats) {
                    search.$or = [
                        {
                            assignedUserId: {
                                $isnull: true,
                            },
                            assignedTeamId: {
                                $isnull: true,
                            },
                        },
                        {
                            assignedTeamId: {
                                $in: enforcedTeamFilters.value.teamIds,
                            },
                        },
                    ];
                } else if (search?.status !== StatusType.OPEN) {
                    search.assignedTeamId = { $in: enforcedTeamFilters.value.teamIds };
                }
            }
            if (filters.tags.length > 0) {
                search["chatTags.tagId"] = { $in: filters.tags };
            }
            if (activeFilter.value.keyword.length > 0) {
                search.keyword = activeFilter.value.keyword;
            }

            // @todo when refactoring filters, there should be a static helper to do the same as below.
            const req = RequestQueryBuilder.create({
                search,
            });

            req.queryObject.chatListMode = (
                activeFilter.value.status?.name === StatusFilterNameType.ARCHIVE ? "newestStatus" : sortBy.value
            ) as ChatListMode;

            if (isFirstRun.value) {
                // @todo when initially loading a chat from another status filter, we don't need to fetch for "new" initially to avoid duplicate requests.
                chatsStore.fetchChatsList(req, Number(router.currentRoute.query.page ?? 1)).then(() => {
                    // If chats are loaded, we know the number of pages, so we can switch to that page in the paginator if page-query in the URL exists.
                    if (router.currentRoute.query.page) {
                        chatsStore.chatsListPagination.page = router.currentRoute.query.page;
                    }
                });
                isFirstRun.value = false;
                return;
            }

            debouncedFetchChatsAndContacts(req, 1);
        };

        watch(
            () => [sortBy, activeFilter, filters],
            ([sortBy, activeFilter, filters]) => {
                applySearchAndFilters({ sortBy, activeFilter, filters });
            },
            { immediate: true, deep: true }
        );

        //----------------------------------------------------------------------
        const restoreDefaultSettings = () => {
            const { getUserSetting } = useUserSettings();
            const settingIsOpen = Boolean(Number(getUserSetting("rightSideMenuIsOpen")));

            const { setCollapsed, setRightCollapsed } = useVerticalNavMenu();
            setCollapsed(settingIsOpen);
            setRightCollapsed(settingIsOpen);
        };

        const openChat = async (chatId: number): Promise<ChatsListChat> => {
            // Prevent opening the current chat again.
            if (chatsStore.activeChat?.chat.id === chatId) {
                return;
            }

            chatsStore.closeChat(chatId);

            const { resetInput, message } = useChatInput();
            resetInput();

            if (chatId !== router.currentRoute.params.chatId) {
                router.replace({
                    name: "messaging-inbox",
                    params: {
                        chatId,
                    },
                    query: {
                        ...router.currentRoute.query,
                    },
                });
            }

            restoreDefaultSettings();

            const activeChat = await chatsStore.fetchChat(chatId).catch(() => {
                chatsStore.isLoadingChat = false;

                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("entities.conversation.Conversation"),
                        text: i18n.t("entities.conversation.could-not-open"),
                        icon: "times",
                        variant: "error",
                    },
                });

                router.replace({
                    name: "messaging-inbox",
                });
            });

            if (!activeChat) {
                return;
            }

            //------------------------------------------------------------------

            message.value = activeChatMessage.value?.message ?? "";

            //------------------------------------------------------------------

            nextTick(scrollToBottomInChatLog);

            //------------------------------------------------------------------

            if (!activeChat.chat.read) {
                const { read } = useReadChat();
                read(activeChat.chat.id, { showToast: false }).catch(() => null);
            }

            mqShallShowSidebarLeft.value = false;

            return activeChat.chat;
        };

        //----------------------------------------------------------------------

        const initialLoad = ref(true); // We need to keep track of initial loading state, otherwise "openChat" will change the url and retrigger this watcher which results in double loading a chat
        watch(
            routeUrl,
            (params) => {
                if (!params.chatId || !initialLoad.value) {
                    initialLoad.value = false;
                    return;
                }

                openChat(params.chatId).then(() => {
                    const { switchStatusFilter } = useMessagingStatusFilters();
                    switchStatusFilter({
                        assignedUserId: chatsStore.activeChat.chat.assignedUserId,
                        assignedTeamId: chatsStore.activeChat.chat.assignedTeamId,
                        status: chatsStore.activeChat.chat.status,
                    });

                    // Somehow after opening chat, the scroll will not reach the fully end of the chat log...
                    // EDIT: This watcher has been executed earlier than the ref element has been created in the template, so its undefined yet.
                    nextTick(scrollToBottomInChatLog);

                    initialLoad.value = false;
                });
            },
            { immediate: true }
        );

        //----------------------------------------------------------------------

        return {
            applySearchAndFilters,
            chatLogPs,
            chatsStore,
            filters,
            mobilePluginBarActive,
            mqShallShowSidebarLeft,
            openChat,
            scrollToBottomInChatLog,
        };
    },
});
